import { main } from './main.js'
// Bare shared-library import, NOT per-file deep
// '@symbo-ls/workspace-shared/pages/*.js' subpaths (CAT-1, tickets/opus.md).
// frank's `declaredLibResolution` only resolves a deep subpath whose first
// segment is `functions/`, `components/` or `designSystem/` — there is NO
// `pages` bag — so a `pages/…` specifier returns null and frank's
// serialization stubs it to an inert marker once this package is pushed as a
// library. Shared exports its pages as a NAMESPACE keyed by export name
// (shared/context.js), so they read off `_wsShared.pages.*` — but only ever
// inside a deferred `lazyPage`/`lazySharedLibPage` loader, never at module
// scope: a direct property read (or a call routed through the stub) is
// itself a stub-phantom-value that publishes as `""`
// (WS-CANVAS-HOLLOW-SHARED-PAGES-BLOCK-PUSH-1). `./lazyPage.js` carries the
// real local implementation — see its header for the mechanism. Same idiom
// as workspace/pages/index.js, which was swept for this exact reason.
import _wsShared from '@symbo-ls/workspace-shared'
import {
  lazyPage,
  lazyStudioDashboardPage,
  lazyStudiosBoardPage,
  lazyCanvasDashboardPage
} from './lazyPage.js'
// /team-and-agents lives in canvas because it's a panzoom + nodes
// canvas (people + agents instead of project tiles). Re-exported here
// so the workspace shell can import it for its own router.
import {
  teamAndAgents,
  TeamPersonNode,
  TeamAgentNode,
  TeamMemberModal,
  AgentModal,
  AgentPermChip,
  AgentToolRow,
  AgentAssigneeRow,
  AgentAssignChip
} from './team-and-agents.js'
export {
  teamAndAgents,
  TeamPersonNode,
  TeamAgentNode,
  TeamMemberModal,
  AgentModal,
  AgentPermChip,
  AgentToolRow,
  AgentAssigneeRow,
  AgentAssignChip
}
// /agents — agents-only view of the same canvas (filter toolbar + no people).
import { agents } from './agents.js'
export { agents }
import { account } from './account.js'
import { marketplace } from './marketplace.js'
import { sessionBridge } from './session-bridge.js'
import { notFound } from './notFound.js'
// Shared auth/waitlist page objects off the bare-import namespace — see the
// header note. `signin`/`signup`/`signupConfirmation`/`resetPassword` reach
// the barrel via shared/pages/identity-index.js; `waitlist` via
// shared/pages/waitlist.js. Each read is deferred inside a `lazyPage`
// loader (never at module scope — see ./lazyPage.js's header). `_wsShared`
// rides this package's own eager sharedLibraries merge, so `_wsShared.pages`
// is already populated by the time any of these mount — the loader's
// `Promise.resolve` settles on the next microtask, before the next paint,
// same trick already proven live in workspace/pages/index.js.
export const signin = lazyPage(
  () => Promise.resolve(_wsShared.pages.signin),
  (el) => ({
    title: el.call('polyglot', 'canvas.page.signin.title')
  })
)
export const signup = lazyPage(
  () => Promise.resolve(_wsShared.pages.signup),
  (el) => ({
    title: el.call('polyglot', 'canvas.page.signup.title')
  })
)
export const signupConfirmation = lazyPage(
  () => Promise.resolve(_wsShared.pages.signupConfirmation),
  (el) => ({ title: el.call('polyglot', 'canvas.page.signupConfirmation.title') })
)
export const resetPassword = lazyPage(
  () => Promise.resolve(_wsShared.pages.resetPassword),
  (el) => ({
    title: el.call('polyglot', 'canvas.page.resetPassword.title')
  })
)
export const waitlist = lazyPage(
  () => Promise.resolve(_wsShared.pages.waitlist),
  (el) => ({
    title: el.call('polyglot', 'canvas.page.waitlist.title')
  })
)
// The canonical signed-in-user account screen (MemberProfile — profile
// fields, availability, equity, notifications, sessions, danger zone).
// Reachable here now that MemberProfile itself moved into `shared`
// (2026-08-15, tickets/opus.md "extract MemberProfile into packages/shared"
// — canvas already depends on shared, so this needed no new cross-package
// dependency). Replaces canvas's own self-contained account.js below.
export const profile = lazyPage(
  () => Promise.resolve(_wsShared.pages.profile),
  (el) => ({
    title: el.call('polyglot', 'canvas.page.account.title')
  })
)

// Signed in = the boot gate's verdict (shared bootResolve sets
// `context.bootSignedIn` from the server's answer), never the raw stored token:
// a stored token can be expired or revoked. The parcel boot settles the gate
// before create(), so the verdict is there at render; a boot still in flight
// is awaited (`context.bootReady`). A host with no boot gate at all (an embed)
// keeps the token answer.
export const rootRedirect = {
  onRender: (el) => {
    const ctx = el.context || {}
    const decide = () => {
      // after the boot the LIVE session decides (root._authReady): an
      // in-place sign-in or a soft sign-out (one-boot review D4)
      const st = el.state && (el.state.root || el.state)
      const live = st && st._authReady === true && typeof st.signedIn === 'boolean'
      const signedIn =
        typeof ctx.bootSignedIn === 'boolean'
          ? live
            ? st.signedIn
            : ctx.bootSignedIn
          : !!ctx.methods?.sdk?.getAuthToken?.()
      el.router(signedIn ? '/studio' : '/signin', el.getRoot())
    }
    if (typeof ctx.bootSignedIn !== 'boolean' && ctx.bootReady) return ctx.bootReady.then(decide)
    decide()
  }
}

// /dashboard is retired (2026-06-10 per Nika "remove /dashboard page and
// reroute all references to it to / page"). Old links/bookmarks bounce to
// `/` — home in the unified shell; the standalone canvas app's rootRedirect
// then resolves it to /studio.
export const dashboardRedirect = {
  onRender: (el) => el.router('/', el.getRoot())
}

export const studioRoute = lazyStudioDashboardPage((el) => ({
    title: el.call('polyglot', 'canvas.page.dashboard.title')
  }))
export const studioBoardRoute = lazyStudiosBoardPage((el) => ({
    title: el.call('polyglot', 'canvas.page.workspaces.fallbackTitle')
  }))
export const canvasRoute = lazyCanvasDashboardPage({ title: 'Canvas / Symbols.app' })

// Every route value is a NAMED export too: canvas/context.js builds its
// published `pages` bag from these bindings (frank 4ed5a2372 proves a
// library subpath import only against a bag built from namespace imports
// and imported bindings).
export { main, account, marketplace, sessionBridge, notFound }

export default {
  '/': rootRedirect,
  '/dashboard': dashboardRedirect,
  '/account': account,
  // SETTINGS-IA-1 (tickets/sonnet.md) found the routing bug — the
  // avatar-menu "Settings" action (shared/functions/accountMenu.js
  // accountMenuAction) does a plain `this.router('/settings', …)`, and
  // canvas had no matching entry so the click fell through to the `/*`
  // catch-all (the project-canvas route). That bug was fixed by pointing
  // this entry at canvas's own self-contained account.js (workspace
  // 436b0772). It's since been superseded: MemberProfile (the shell's
  // fuller canonical account page — profile fields, availability, equity,
  // notifications, sessions, danger zone, language/timezone/avatar-upload)
  // moved into `shared` (tickets/opus.md, 2026-08-15 Nika ruling), so
  // canvas now reaches the SAME implementation the shell does instead of
  // maintaining a second, parallel one. account.js itself stays for now —
  // still reachable at /account.
  '/settings': profile,
  '/marketplace': marketplace,
  '/_session-bridge': sessionBridge,
  // Bare `/studio` mounts the dashboard surface directly (the project picker) —
  // the canonical entry-point the nav link points at (#2212). lazySharedLibPage
  // (not lazyPage): the dashboard embeds the studios board (`StudiosEmbed: {}`)
  // and resolves studios-only components, so studios' 19 self-registered
  // components (its default export) must fold into the live context BEFORE the
  // dashboard mounts. Both the studios chunk (context) and the dashboard chunk
  // load on first /studio nav; the eager bundle carries neither (#11 Stage 2 —
  // studios' 19 components self-register via its default export on lazy load).
  '/studio': studioRoute,
  // The studios board itself (project canvas). lazySharedLibPage folds studios'
  // component registry (default export) then mounts the `studios` board page, so
  // its childExtends 'ProjectNode' / 'IntegrationNode' / … resolve against the
  // now-populated context. The board's own dynamic metadata re-applies on mount;
  // this static title is the pre-resolve placeholder helmet shows.
  '/studio/*': studioBoardRoute,
  // /canvas — duplicate of the /studio dashboard surface. Same lazySharedLibPage
  // shape as /studio (folds the studios component registry so the embedded
  // StudiosEmbed board resolves); only the tab title diverges.
  // Pure product names — nothing in this title is translatable.
  '/canvas': canvasRoute,
  '/signin': signin,
  '/signup': signup,
  '/signup-confirmation': signupConfirmation,
  '/reset-password': resetPassword,
  '/waitlist': waitlist,
  '/404': notFound,
  '/*': main
}
